import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/sonner';
import { tripsAPI } from '../../lib/api';
import { messageErreur } from '../../lib/erreurs';
import { libelle, optionsLibelles } from '../../lib/libelles';
import { lotsAPI, nouvelleCleLot } from '../../lib/lotsApi';
import { ETAPES_CONFIRMATION } from '../../lib/lotsFiltres';
import { formatVolume } from '../../lib/utils';

const ETAPE_SORTIE_DOUANE = 'CLEARED_CUSTOMS_ENTRY';

const nombreOuNull = (texte) => {
  const brut = String(texte ?? '').replace(/[\s ]/g, '').replace(',', '.');
  if (brut === '') return null;
  const valeur = Number(brut);
  return Number.isFinite(valeur) ? valeur : null;
};

/**
 * Confirmation groupée : les camions cochés prennent l'étape annoncée par le
 * partenaire. Chaque camion devient un trajet posé à cette étape, avec le
 * dépôt, le N° de bon, la date de chargement et la quantité chargée.
 *
 * Un camion refusé n'empêche pas les autres : le serveur rend les refus avec
 * leur phrase, la page les affiche (`onDone`).
 */
export const ConfirmerCamionsDialog = ({ open, onOpenChange, lotId, reference, depotParDefaut, lignes = [], onDone }) => {
  const [etape, setEtape] = useState(ETAPES_CONFIRMATION[0]);
  const [depot, setDepot] = useState('');
  const [bon, setBon] = useState('');
  const [dateChargement, setDateChargement] = useState('');
  const [docSortie, setDocSortie] = useState('');
  const [dateSortieDouane, setDateSortieDouane] = useState('');
  const [quantite, setQuantite] = useState('');
  const [parLigne, setParLigne] = useState({});
  const [detailOuvert, setDetailOuvert] = useState(false);
  const [depots, setDepots] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(null);

  useEffect(() => {
    if (!open) return;
    setEtape(ETAPES_CONFIRMATION[0]);
    setDepot(depotParDefaut || '');
    setBon('');
    setDateChargement('');
    setDocSortie('');
    setDateSortieDouane('');
    setQuantite('');
    setParLigne({});
    setDetailOuvert(false);
    setEnCours(false);
    cle.current = nouvelleCleLot('lot-confirmation');
    Promise.resolve(tripsAPI.getLoadingDepots())
      .then((liste) => setDepots(Array.isArray(liste) ? liste : []))
      .catch(() => setDepots([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const changerLigne = (id, champ, valeur) =>
    setParLigne((avant) => ({ ...avant, [id]: { ...(avant[id] || {}), [champ]: valeur } }));

  const confirmer = async (evenement) => {
    evenement.preventDefault();
    if (enCours || lignes.length === 0) return;
    const corps = { line_ids: lignes.map((ligne) => ligne.id), stage: etape };
    if (depot.trim()) corps.loading_depot = depot.trim();
    if (bon.trim()) corps.loading_voucher_number = bon.trim();
    if (dateChargement) corps.loading_date = dateChargement;
    if (docSortie.trim()) corps.exit_document_number = docSortie.trim();
    if (etape === ETAPE_SORTIE_DOUANE && dateSortieDouane) corps.customs_exit_date = dateSortieDouane;
    const commune = nombreOuNull(quantite);
    if (commune !== null) corps.loaded_quantity = commune;

    const propres = {};
    lignes.forEach((ligne) => {
      const saisie = parLigne[ligne.id] || {};
      const valeurs = {};
      const chargee = nombreOuNull(saisie.loaded_quantity);
      if (chargee !== null) valeurs.loaded_quantity = chargee;
      if (String(saisie.loading_voucher_number || '').trim()) {
        valeurs.loading_voucher_number = String(saisie.loading_voucher_number).trim();
      }
      if (Object.keys(valeurs).length > 0) propres[ligne.id] = valeurs;
    });
    if (Object.keys(propres).length > 0) corps.per_line = propres;

    setEnCours(true);
    try {
      const resultat = await lotsAPI.confirm(lotId, corps, cle.current);
      cle.current = null;
      onDone?.(resultat);
      onOpenChange(false);
    } catch (err) {
      toast.error(messageErreur(err, "Les camions n'ont pas pu être confirmés"));
    } finally {
      setEnCours(false);
    }
  };

  const etapes = optionsLibelles('lot_etape_confirmation', ETAPES_CONFIRMATION);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="lot-confirmation-dialog">
        <DialogHeader>
          <DialogTitle>
            Confirmer {lignes.length} camion{lignes.length > 1 ? 's' : ''}
            {reference ? ` du lot ${reference}` : ''}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choisir l’étape annoncée et renseigner le chargement des camions cochés.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={confirmer} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label>Étape *</Label>
              <Select value={etape} onValueChange={setEtape}>
                <SelectTrigger className="input-base" aria-label="Étape" data-testid="confirmation-etape">
                  <SelectValue placeholder="Étape" />
                </SelectTrigger>
                <SelectContent>
                  {etapes.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirmation-depot">Dépôt de chargement</Label>
              <Input
                id="confirmation-depot"
                list="confirmation-depots"
                value={depot}
                onChange={(evenement) => setDepot(evenement.target.value)}
                className="input-base uppercase"
                data-testid="confirmation-depot"
              />
              <datalist id="confirmation-depots">
                {depots.map((nom) => (
                  <option key={nom} value={nom} />
                ))}
              </datalist>
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirmation-bon">N° de bon</Label>
              <Input
                id="confirmation-bon"
                value={bon}
                onChange={(evenement) => setBon(evenement.target.value)}
                className="input-base"
                data-testid="confirmation-bon"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirmation-date-chargement">Date de chargement</Label>
              <Input
                id="confirmation-date-chargement"
                type="date"
                value={dateChargement}
                onChange={(evenement) => setDateChargement(evenement.target.value)}
                className="input-base"
                data-testid="confirmation-date-chargement"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirmation-quantite">Quantité chargée (L)</Label>
              <Input
                id="confirmation-quantite"
                inputMode="decimal"
                value={quantite}
                onChange={(evenement) => setQuantite(evenement.target.value)}
                placeholder="Selon la quantité prévue"
                className="input-base text-right tabular-nums"
                data-testid="confirmation-quantite"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirmation-doc-sortie">Document de sortie</Label>
              <Input
                id="confirmation-doc-sortie"
                value={docSortie}
                onChange={(evenement) => setDocSortie(evenement.target.value)}
                className="input-base"
                data-testid="confirmation-doc-sortie"
              />
            </div>
            {etape === ETAPE_SORTIE_DOUANE ? (
              <div className="space-y-1">
                <Label htmlFor="confirmation-date-douane">Date de sortie douane</Label>
                <Input
                  id="confirmation-date-douane"
                  type="date"
                  value={dateSortieDouane}
                  onChange={(evenement) => setDateSortieDouane(evenement.target.value)}
                  className="input-base"
                  data-testid="confirmation-date-douane"
                />
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDetailOuvert((avant) => !avant)}
              aria-expanded={detailOuvert}
              data-testid="confirmation-detail"
            >
              {detailOuvert ? (
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              )}
              Détail par camion ({lignes.length})
            </Button>
            {detailOuvert ? (
              <div className="max-h-56 overflow-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/80 text-xs text-muted-foreground backdrop-blur">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium" scope="col">Tracteur</th>
                      <th className="px-2 py-1.5 text-right font-medium" scope="col">Quantité chargée (L)</th>
                      <th className="px-2 py-1.5 text-left font-medium" scope="col">N° de bon</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {lignes.map((ligne) => (
                      <tr key={ligne.id}>
                        <td className="px-2 py-1 font-mono font-semibold">{ligne.tractor_plate}</td>
                        <td className="px-1.5 py-1">
                          <Input
                            inputMode="decimal"
                            value={parLigne[ligne.id]?.loaded_quantity ?? ''}
                            onChange={(evenement) => changerLigne(ligne.id, 'loaded_quantity', evenement.target.value)}
                            placeholder={ligne.expected_quantity ? formatVolume(ligne.expected_quantity) : ''}
                            aria-label={`Quantité chargée de ${ligne.tractor_plate}`}
                            className="h-8 px-2 text-right text-sm tabular-nums"
                          />
                        </td>
                        <td className="px-1.5 py-1">
                          <Input
                            value={parLigne[ligne.id]?.loading_voucher_number ?? ''}
                            onChange={(evenement) =>
                              changerLigne(ligne.id, 'loading_voucher_number', evenement.target.value)
                            }
                            aria-label={`N° de bon de ${ligne.tractor_plate}`}
                            className="h-8 px-2 text-sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={enCours}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || lignes.length === 0} data-testid="confirmation-valider">
              {enCours ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {`Confirmer à « ${libelle('lot_etape_confirmation', etape)} »`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmerCamionsDialog;
